import { useState, useEffect, useCallback, useRef } from 'react';
import { getSocket, emitAck } from '../api/socket.js';
import { http } from '../api/http.js';
import { getTeacherToken } from '../hooks/useTeacherAuth.js';

const EMPTY_PULSE = { understand: { ok: 0, confused: 0 }, hands: [] };

/**
 * All of the teacher's live wiring for one room in one hook: auth, the
 * question/leaderboard/player sync loop, the classroom pulse (understand
 * meter + raised hands), emoji reactions, and the action emitters. The deck
 * UI stays a pure function of what this returns.
 */
export function useRoomChannel(roomId) {
  const token = getTeacherToken();

  const [authState, setAuthState] = useState(token ? 'checking' : 'unauthenticated');
  const [roomCode, setRoomCode] = useState('');
  const [status, setStatus] = useState('lobby');
  const [question, setQuestion] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [players, setPlayers] = useState([]);
  const [questions, setQuestions] = useState([]); // full list w/ payloads, for quiz previews
  const [pulse, setPulse] = useState(EMPTY_PULSE);
  const [reactions, setReactions] = useState([]); // [{id, emoji}] transient
  const [busy, setBusy] = useState(false);
  // Where the deck stands, as the server sees it. `undefined` means nothing
  // has arrived yet; `null` means the server has been asked and holds none
  // (it restarted), which is the only moment a screen may offer its own
  // remembered position. See useDeckPosition.
  const [position, setPosition] = useState(undefined);
  const [connected, setConnected] = useState(false);

  // Load the room's full question list once (payloads included — teacher only).
  // Send the token explicitly so this works whether or not the auth cookie
  // is present on this origin.
  useEffect(() => {
    if (!token) return;
    http
      .get(`/api/rooms/${roomId}`, { Authorization: `Bearer ${token}` })
      .then((res) => setQuestions(res.questions || []))
      .catch(() => {});
  }, [roomId, token]);

  useEffect(() => {
    if (!token) {
      setAuthState('unauthenticated');
      return undefined;
    }
    const socket = getSocket();

    const bind = () => {
      emitAck('teacher:auth', { jwt: token, roomId: Number(roomId) }).then((res) => {
        if (res.ok) {
          setAuthState('ok');
          if (res.roomCode) setRoomCode(res.roomCode);
          if (res.status) setStatus(res.status);
          if (res.players) setPlayers(res.players);
        } else {
          setAuthState('unauthenticated');
        }
      });
    };

    const onSync = (state) => {
      setStatus(state.status);
      if (state.roomCode) setRoomCode(state.roomCode);
      setQuestion(state.currentQuestion);
      setLeaderboard(state.leaderboard || []);
      if (state.players) setPlayers(state.players);
    };
    const onTally = (data) => {
      setQuestion((q) =>
        q ? { ...q, tally: data.tally, answeredCount: data.answeredCount, totalPlayers: data.totalPlayers } : q
      );
    };
    const onLeaderboard = ({ leaderboard: lb }) => setLeaderboard(lb);
    const onPlayers = ({ players: list }) => setPlayers(list);
    const onPulse = (p) => setPulse(p && p.understand ? p : EMPTY_PULSE);
    const onDeckPosition = (pos) => setPosition(pos || null);
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onReact = ({ emoji }) => {
      const id = Math.random().toString(36).slice(2);
      setReactions((r) => [...r, { id, emoji }]);
      setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 3200);
    };

    socket.on('connect', bind);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('deck:position', onDeckPosition);
    socket.on('state:sync', onSync);
    socket.on('answer:tally', onTally);
    socket.on('leaderboard:update', onLeaderboard);
    socket.on('player:list', onPlayers);
    socket.on('room:pulse', onPulse);
    socket.on('room:react', onReact);
    if (socket.connected) {
      setConnected(true);
      bind();
    }

    return () => {
      socket.off('connect', bind);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('deck:position', onDeckPosition);
      socket.off('state:sync', onSync);
      socket.off('answer:tally', onTally);
      socket.off('leaderboard:update', onLeaderboard);
      socket.off('player:list', onPlayers);
      socket.off('room:pulse', onPulse);
      socket.off('room:react', onReact);
    };
  }, [token, roomId]);

  const run = useCallback(async (event, payload) => {
    setBusy(true);
    try {
      return await emitAck(event, payload || {});
    } finally {
      setBusy(false);
    }
  }, []);

  const actions = useRef(null);
  if (!actions.current) {
    actions.current = {
      openQuestion: (index) => run('question:jump', { index }),
      reveal: () => run('question:reveal', {}),
      nextQuestion: () => run('question:next', {}),
      prevQuestion: () => run('question:prev', {}),
      reset: () => run('room:reset', {}),
      awardStar: (playerId) => emitAck('star:award', { playerId }),
      kickPlayer: (playerId) => run('player:kick', { playerId }),
      newSlide: () => emitAck('teacher:signal', { kind: 'slide' }),
      // Absolute, never a delta: see the server's deckPosition.js for why.
      // Not routed through `run`, because putting the deck into the busy
      // state on every arrow press would grey out the quiz buttons as the
      // instructor walks the slides.
      gotoSlide: (index, substep = 0) => emitAck('deck:goto', { index, substep }),
      setOverlay: (overlay) => emitAck('deck:overlay', { overlay: overlay ?? null }),
    };
  }

  const connectedCount = players.filter((p) => p.connected).length;

  return {
    authState,
    roomId,
    roomCode: roomCode || String(roomId),
    status,
    question,
    questions,
    leaderboard,
    players,
    connectedCount,
    pulse,
    reactions,
    busy,
    position,
    connected,
    actions: actions.current,
  };
}
